<script>
  /**
   * Unified canvas toolbar for block and embed controls
   * Uses CSS anchor positioning with popover top-layer for both sections:
   * - Block tools: anchored to the selected block (--selected-block)
   * - Embed tools: anchored to the hovered preview card (dynamic anchor name)
   */
  import { getContext, untrack } from 'svelte';
  import { page } from '$app/state';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import FieldVisibilityMenu from '#lib/FieldVisibilityMenu.svelte';
  import Divider from '#lib/Divider.svelte';
  import Switch from '#lib/Switch.svelte';
  import ColorThumbnail from '#lib/ColorThumbnail.svelte';
  import { lazy } from '#lib/lazy.svelte.js';
  const entry_picker_drawer = lazy(() => import('#lib/EntryPickerDrawer.svelte'));
  import { get_visibility_icon, get_site_color_options, fetch_entries, nanoid } from '#lib/util.js';
  import { register_click_outside_element } from '#lib/click_outside.svelte.js';
  import t from '#lib/i18n.svelte.js';
  
  let site_settings = getContext('site_settings');

  let {
    // Block tools props
    /** @type {HTMLElement} The block element (used to verify block is ready) */
    block_anchor,
    /** @type {number} Current block index */
    block_index = 0,
    /** @type {number} Total number of blocks */
    total_blocks = 0,
    /** @type {(direction: 'up'|'down') => void} Callback when block move is triggered */
    onmove,
    
    // Embed tools props
    /** @type {Array<{index: number, anchor: string, visibility: string}>} One toolbar per slot.
        Persistent layouts pass every preview card; overlapping ones pass the hovered card only. */
    embed_slots = [],
    /** @type {boolean} True when every card carries its own button (dimmed until hovered) */
    embed_tools_persistent = true,
    /** @type {number|null} Index of the currently hovered preview card, for emphasis */
    embed_hovered_index = null,
    /** @type {number} Total number of entry previews */
    embed_total = 0,
    /** @type {'horizontal' | 'vertical'} Layout orientation for embed tools */
    embed_orientation = 'vertical',
    /** @type {boolean} Whether this is a fullscreen layout (toolbars need offset) */
    is_fullscreen_layout = false,
    /** @type {(index: number, target: number) => void} Move embed callback (from → to index) */
    on_embed_move,
    /** @type {(index: number) => void} Edit embed callback */
    on_embed_edit,
    /** @type {(index: number) => void} Remove embed callback */
    on_embed_remove,
    /** @type {(hovering: boolean) => void} Callback when embed toolbar hover state changes */
    on_embed_toolbar_hover,
    /** @type {(open: boolean) => void} Callback when embed menu open state changes */
    on_embed_menu_open,
  } = $props();

  let entry_api = getContext('entry_api');
  let container = $derived(entry_api?.active_container);
  let block = $derived(container?.selected_block);
  
  // Menu refs and state
  let block_menu_ref = $state();
  let embed_menu_refs = $state([]);
  let block_menu_open = $state(false);
  // Only one embed menu is open at a time; null when none is.
  let embed_menu_open_index = $state(null);
  
  // Popover refs: both toolbars live in the top layer, or the per-card ones would
  // slide under the DynamicIsland while the block toolbar rides over it.
  let block_toolbar_ref = $state();
  let embed_toolbar_refs = $state([]);

  // Navigation state - block
  let can_move_up = $derived(block_index > 0);
  let can_move_down = $derived(block_index < total_blocks - 1);
  
  // Navigation state - embed
  let is_horizontal = $derived(embed_orientation === 'horizontal');
  let prev_icon = $derived(is_horizontal ? 'arrow-left' : 'small-arrow-up');
  let next_icon = $derived(is_horizontal ? 'arrow-right' : 'small-arrow-down');
  let prev_label = $derived(is_horizontal ? t('move_left') : t('move_up'));
  let next_label = $derived(is_horizontal ? t('move_right') : t('move_down'));

  // Color options for block color picker
  let color_options = $derived(
    site_settings?.colors?.site_colors 
      ? get_site_color_options(site_settings.colors.site_colors) 
      : []
  );
  
  // For multi-selection color indicator
  // Returns { color, is_mixed } where color is the shared color (null = no color) and is_mixed indicates different colors
  let selected_color_info = $derived.by(() => {
    const blocks = container?.selected_blocks?.() ?? [];
    if (blocks.length === 0) return { color: null, is_mixed: false };
    const first_color = blocks[0]?.color ?? null;
    const all_same = blocks.every(b => (b?.color ?? null) === first_color);
    return { color: first_color, is_mixed: !all_same };
  });
  
  // Check if all embedded entries are images (don't show link toggle for images)
  let all_embeds_are_images = $derived(
    block?.entry_previews?.length > 0 &&
    block.entry_previews.every(preview => preview.type === 'image')
  );

  // ——— Per-placement image fit (crop is a decision about a slot, not the image) ———
  function embed_entry_at(index) {
    return block?.entry_previews?.[index];
  }

  function embed_slot_key_at(index) {
    const entry = embed_entry_at(index);
    return entry != null ? String(entry.entry_id) : null;
  }

  function embed_slot_fit_at(index) {
    const key = embed_slot_key_at(index);
    return key != null
      ? (block?.entry_preview_settings?.[key]?.fit ?? 'auto')
      : 'auto';
  }

  function set_embed_slot_fit(index, value) {
    const embed_slot_key = embed_slot_key_at(index);
    if (!block || embed_slot_key == null) return;
    const settings = { ...(block.entry_preview_settings || {}) };
    const existing = { ...(settings[embed_slot_key] || {}) };
    if (value === 'auto') {
      delete existing.fit; // back to the media entry's own setting
    } else {
      existing.fit = value;
    }
    if (Object.keys(existing).length === 0) {
      delete settings[embed_slot_key];
    } else {
      settings[embed_slot_key] = existing;
    }
    block.entry_preview_settings = settings;
  }

  // ——— Block slot ratio: the automatic average is now visible and pinnable ———
  function set_block_ratio(value) {
    const blocks = container?.selected_blocks?.() ?? [];
    for (const b of blocks) {
      if (b.type === 'story') b.aspect_ratio = value;
    }
  }
  
  function select_color(value) {
    const blocks = container?.selected_blocks?.() ?? [];
    for (const b of blocks) {
      b.color = value || null;
    }
  }

  // Show block toolbar popover when block is selected
  // Re-run when block_index changes (e.g., after move up/down)
  $effect(() => {
    const _ = block_index; // dependency to re-trigger on move
    if (block_anchor && block_toolbar_ref) {
      block_toolbar_ref.showPopover();
      return () => {
        try { block_toolbar_ref?.hidePopover(); } catch {}
      };
    }
  });

  // Same for the per-card toolbars — one popover each, re-shown when the slot
  // list changes. Keyed on the anchor names rather than the array: hovering a
  // preview rebuilds `embed_slots`' identity on every enter/leave, and hiding +
  // re-showing every popover for that would churn the top layer (and, with the
  // site's :has() rules, force a full style recalc) on the mousemove hot path.
  let embed_slot_keys = $derived(embed_slots.map(slot => slot.anchor).join(' '));
  $effect(() => {
    embed_slot_keys;
    const els = untrack(() => embed_slots)
      .map(slot => embed_toolbar_refs[slot.index])
      .filter(Boolean);
    for (const el of els) {
      try { el.showPopover(); } catch { /* already open */ }
    }
    return () => {
      for (const el of els) {
        try { el.hidePopover(); } catch { /* already closed */ }
      }
    };
  });

  // Close menus on click outside
  $effect(() => {
    if (block_menu_open && block_menu_ref) {
      return register_click_outside_element({
        el: block_menu_ref,
        on_click_outside: () => block_menu_open = false
      });
    }
  });
  
  $effect(() => {
    const el = embed_menu_open_index !== null ? embed_menu_refs[embed_menu_open_index] : null;
    if (el) {
      return register_click_outside_element({
        el,
        on_click_outside: () => embed_menu_open_index = null
      });
    }
  });

  // Notify context when menus open/close
  $effect(() => {
    const active = block_menu_open ? 'block' : embed_menu_open_index !== null ? 'embed' : null;
    entry_api?.set_active_canvas_menu?.(active);
  });

  // Notify parent when embed menu opens/closes (to lock toolbar position)
  $effect(() => {
    on_embed_menu_open?.(embed_menu_open_index !== null);
  });

  // Block actions
  function duplicate_block() {
    block_menu_open = false;
    container?.duplicate_selected_blocks();
  }

  // Sticker: pick an image, then attach it as a decorative sticker to the block
  // (see StoryBlockModel: block.stickers; edited in-canvas via StickerEditor)
  let sticker_picker_open = $state(false);

  function add_sticker() {
    block_menu_open = false;
    sticker_picker_open = true;
  }

  async function handle_sticker_image_confirm(selected_ids) {
    sticker_picker_open = false;
    if (!block || !selected_ids?.length) return;
    const [preview] = await fetch_entries([selected_ids[0]], { as_preview: true });
    const image = preview?.images?.[0];
    if (!image?.src) return;
    block.stickers = [
      ...(block.stickers ?? []),
      {
        id: nanoid(),
        src: image.src,
        width: image.width,
        height: image.height,
        anchor: block.entry_previews?.length > 0 ? 0 : 'block',
        area: 'bottom-right',
        size: 120,
        rotate: 0,
        offset_x: 0,
        offset_y: 0,
      },
    ];
  }

  function delete_block() {
    block_menu_open = false;
    container?.delete_block();
  }

  // Embed actions
  function edit_embed(index) {
    embed_menu_open_index = null;
    on_embed_edit?.(index);
  }

  function remove_embed(index) {
    embed_menu_open_index = null;
    on_embed_remove?.(index);
  }
  
  /**
   * Reordering is a repeated action, so the menu stays open and follows the card it moved -
   * the toolbars are anchored per position, so the moved card's menu is the one at the target
   * index. Overlapping layouts render only the hovered slot, so there the menu has to close.
   */
  function move_embed(index, direction) {
    // The menu entries are already bounds-guarded; the mutation clamps once more
    const target = direction === 'prev' ? index - 1 : index + 1;
    on_embed_move?.(index, target);
    embed_menu_open_index = embed_tools_persistent ? target : null;
  }
</script>

<!-- Reusable snippet for menu action buttons -->
{#snippet menu_action(onclick, icon, label, variant = 'ghostly')}
  <button onclick={onclick}>
    <ButtonLabel {variant} start_icon={icon} align='start-stretch' text_style='text-control'>
      {label}
    </ButtonLabel>
  </button>
{/snippet}

{#snippet block_menu()}
  {@render menu_action(duplicate_block, 'duplicate', t('duplicate'))}
  {#if block?.type === 'story'}
    {@render menu_action(add_sticker, 'star', t('sticker_add'))}
  {/if}
  {@render menu_action(delete_block, 'trash', t('remove_block'), 'destructive ghostly')}
  {#if block}
    <Divider class='px p-2' label={t('block_fields')} />
    <FieldVisibilityMenu {block} context="block" />
  {/if}
  {#if block?.entry_previews?.length > 0}
    <Divider class='px p-2' label={t('aspect_ratio_label')} />
    <div class="ratio-options px p-4 my m-2" role="group" aria-label={t('aspect_ratio_label')}>
      <button
        class="ratio-option"
        class:selected={!block.aspect_ratio}
        onclick={(e) => { e.stopPropagation(); set_block_ratio(null); }}
      >
        <ButtonLabel variant={!block.aspect_ratio ? 'canvas segment-selected' : 'canvas segment'} text_style='text-control'>
          {t('auto')}
        </ButtonLabel>
      </button>
      {#each site_settings?.aspect_ratios || [] as ratio}
        <button
          class="ratio-option"
          class:selected={block.aspect_ratio === ratio}
          onclick={(e) => { e.stopPropagation(); set_block_ratio(ratio); }}
        >
          <ButtonLabel variant={block.aspect_ratio === ratio ? 'canvas segment-selected' : 'canvas segment'} text_style='text-control'>
            {ratio.replace('/', ':')}
          </ButtonLabel>
        </button>
      {/each}
    </div>
  {/if}
  {#if color_options.length > 0}
    <Divider class='px p-2' label={t('colors')} />
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <div class="color-picker px p-4 my m-3" role="group" aria-label={t('colors')} onclick={(e) => e.stopPropagation()} onmousedown={(e) => e.stopPropagation()} onkeydown={(e) => e.stopPropagation()}>
      {#each color_options as option}
        {@const is_selected = !selected_color_info.is_mixed && (selected_color_info.color === option.value || (selected_color_info.color === null && option.value === ''))}
        <button 
          class="color-swatch px py p-1"
          class:selected={is_selected}
          onclick={(e) => { e.stopPropagation(); select_color(option.value); }}
          onmousedown={(e) => e.stopPropagation()}
          title={option.title}
        >
          <ColorThumbnail
            background={option.icon_color2}
            foreground={option.icon_color1}
            emphasis_background={option.background_color}
          />
        </button>
      {/each}
    </div>
  {/if}
{/snippet}

{#snippet embed_menu(index, visibility)}
  <!-- Moving used to be two bare arrows on the toolbar. They are labelled menu entries now:
       one persistent button per card is quiet enough to leave standing, three are not. -->
  {#if index > 0}
    {@render menu_action(() => move_embed(index, 'prev'), prev_icon, prev_label)}
  {/if}
  {#if index < embed_total - 1}
    {@render menu_action(() => move_embed(index, 'next'), next_icon, next_label)}
  {/if}
  {@render menu_action(() => edit_embed(index), 'pencil', t('edit'))}
  {@render menu_action(() => remove_embed(index), 'trash', t('remove'), 'destructive ghostly')}
  <Divider class='px p-2' label={t('entry_preview_fields')} />
  {#if block}
    <FieldVisibilityMenu {block} context="embed" />
    {#if embed_entry_at(index)?.type === 'image'}
      {@const slot_fit = embed_slot_fit_at(index)}
      <Divider class='px p-2' label={t('image_fit')} />
      <div class="fit-options px p-4 my m-2" role="group" aria-label={t('image_fit')}>
        {#each [
          { value: 'auto', label: t('entry_default') },
          { value: 'cover', label: t('crop_to_fill') },
          { value: 'contain', label: t('show_whole_image') },
        ] as option}
          <button
            class="fit-option"
            onclick={() => set_embed_slot_fit(index, option.value)}
          >
            <ButtonLabel
              variant='ghostly'
              align='start-stretch'
              text_style='text-control'
              start_icon={slot_fit === option.value ? 'check' : ' '}
            >
              {option.label}
            </ButtonLabel>
          </button>
        {/each}
      </div>
    {/if}
    {#if !all_embeds_are_images}
      <Switch
        value={block.link_entry_previews}
        toggle={() => { block.link_entry_previews = !block.link_entry_previews }}
        variant='ghostly'
        start_icon='link'
      >
        {t('link_to_full_entries')}
      </Switch>
    {/if}
  {/if}
  <Divider class='px p-2' label={t('visibility')} />
  <ButtonLabel variant='ghostly' start_icon={get_visibility_icon(visibility)} align='start-stretch' text_style='text-control'>
    {t(visibility)}
  </ButtonLabel>
{/snippet}

<!-- Block toolbar: anchored to --selected-block, uses popover for top-layer -->
<div 
  bind:this={block_toolbar_ref}
  class="canvas-toolbar block-toolbar"
  class:menu-open={block_menu_open}
  popover="manual"
>
  {#if can_move_up}
    <button class="toolbar-btn" onclick={() => onmove?.('up')} title={t('move_up')}>
      <ButtonLabel start_icon='small-arrow-up' />
    </button>
  {/if}
  {#if can_move_down}
    <button class="toolbar-btn" onclick={() => onmove?.('down')} title={t('move_down')}>
      <ButtonLabel start_icon='small-arrow-down' />
    </button>
  {/if}
  
  <div class="flex-row relative" bind:this={block_menu_ref}>
    <button
      class="toolbar-btn"
      onclick={() => block_menu_open = !block_menu_open}
      aria-expanded={block_menu_open}
      aria-haspopup="true"
      title={t('more_actions')}
    >
      <ButtonLabel start_icon='dot-dot-dot' />
    </button>
    
    {#if block_menu_open}
      <div class="absolute-popover flex-column" role="menu" tabindex="-1" onkeydown={(e) => e.key === 'Escape' && (block_menu_open = false)}>
        {@render block_menu()}
      </div>
    {/if}
  </div>
</div>

<!-- Embed toolbars: one per preview card, so the affordance is countable and stays put.
     Overlapping layouts pass a single slot and it follows the hovered card instead. -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
{#each embed_slots as slot (slot.anchor)}
  <div
    bind:this={embed_toolbar_refs[slot.index]}
    popover="manual"
    class="canvas-toolbar embed-toolbar"
    class:fullscreen-offset={is_fullscreen_layout}
    class:persistent={embed_tools_persistent}
    class:card-hovered={embed_hovered_index === slot.index}
    class:menu-open={embed_menu_open_index === slot.index}
    style:position-anchor={slot.anchor}
    onmouseenter={() => on_embed_toolbar_hover?.(true)}
    onmouseleave={() => on_embed_toolbar_hover?.(false)}
  >
    <div class="flex-row relative" bind:this={embed_menu_refs[slot.index]}>
      <button
        class="toolbar-btn"
        onclick={(e) => { e.stopPropagation(); embed_menu_open_index = embed_menu_open_index === slot.index ? null : slot.index; }}
        aria-expanded={embed_menu_open_index === slot.index}
        aria-haspopup="true"
        title={t('more_actions')}
      >
        <ButtonLabel start_icon='dot-dot-dot' />
      </button>

      {#if embed_menu_open_index === slot.index}
        <div
          class="absolute-popover flex-column"
          role="menu"
          tabindex="-1"
          onclick={(e) => e.stopPropagation()}
          ondblclick={(e) => e.stopPropagation()}
          onkeydown={(e) => e.key === 'Escape' && (embed_menu_open_index = null)}
        >
          {@render embed_menu(slot.index, slot.visibility)}
        </div>
      {/if}
    </div>
  </div>
{/each}

<!-- Image picker for adding a sticker to the selected block -->
{#if sticker_picker_open && entry_picker_drawer.component}
  <entry_picker_drawer.component
    entry_type='image'
    initial_selected={[]}
    onconfirm={handle_sticker_image_confirm}
    oncancel={() => sticker_picker_open = false}
    user_role={page.data.current_user?.role || 'admin'}
  />
{/if}

<style lang='postcss'>
  .ratio-options {
    display: flex;
    flex-wrap: wrap;
    gap: var(--size-1);
  }
  .fit-options {
    display: flex;
    flex-direction: column;
  }

  .canvas-toolbar {
    /* Popover resets */
    margin: 0;
    border: none;
    padding: 0;
    overflow: visible; /* Allow dropdown to render outside */
    
    /* Layout */
    display: flex;
    flex-direction: row;
    background: var(--color-default-background);
    border-radius: 4px;
    box-shadow: var(--shadow-sm);
    
    /* Visibility */
    opacity: 0.8;
    transition: opacity 0.15s;
    
    &:hover,
    &:focus-within,
    &.menu-open {
      opacity: 1;
    }
  }

  /* Block toolbar: anchored to top-right of selected block */
  .block-toolbar {
    position: fixed;
    position-anchor: --selected-block;
    top: anchor(top);
    left: anchor(right);
    /* Shift left by element width + margin to align right edges */
    transform: translateX(calc(-100% - var(--scrollbar-width) - var(--size-2)));
    margin-block-start: var(--size-2);
  }

  /* Embed toolbar: anchored to top-right of hovered preview card */
  /* position-anchor is set dynamically via style binding */
  .embed-toolbar {
    position: fixed;
    z-index: 100;
    top: anchor(top);
    left: anchor(right);
    /* Shift left by element width + margin to align right edges */
    transform: translateX(calc(-100% - var(--size-2)));
    margin-block-start: var(--size-2);
    
    /* For fullscreen layouts, offset further left to avoid overlapping block toolbar */
    /* Block toolbar width ≈ 3 buttons × (24px + 2×size-3) + scrollbar + margin */
    &.fullscreen-offset {
      --block-toolbar-offset: calc(3 * (24px + 2 * var(--size-3)) + var(--scrollbar-width) + var(--size-2) + var(--size-2));
      transform: translateX(calc(-100% - var(--size-2) - var(--block-toolbar-offset)));
    }

    /* One button per card, standing whether or not the pointer is near it. It sits back at
       low contrast and comes forward on the hovered card - the emphasis changes, never the
       presence, so nothing appears or moves under the cursor. */
    &.persistent {
      opacity: 0.45;

      &.card-hovered,
      &:hover,
      &:focus-within,
      &.menu-open {
        opacity: 1;
      }
    }
  }

  .toolbar-btn {
    --button-outer-spacing: var(--size-3);
    --input-min-height: 24px;
    --input-min-width: 24px;
    flex-shrink: 0;
    width: calc(24px + 2 * var(--size-3));
    height: calc(24px + 2 * var(--size-3));
  }

  .color-picker {
    display: grid;
    gap: var(--size-1);
    max-width: 100%;
    width: 0;
    min-width: 100%;
    grid-template-columns: repeat(3, 1fr); /* default - works for 3, 5, 6, 8, 9, 12 items */
    
    /* Dynamic columns using :last-child to detect exact count */
    &:has(.color-swatch:only-child) { grid-template-columns: 1fr; }
    &:has(.color-swatch:nth-child(2):last-child) { grid-template-columns: repeat(2, 1fr); }
    &:has(.color-swatch:nth-child(4):last-child) { grid-template-columns: repeat(2, 1fr); } /* 2+2 */
    &:has(.color-swatch:nth-child(7):last-child) { grid-template-columns: repeat(4, 1fr); } /* 4+3 */
    &:has(.color-swatch:nth-child(10):last-child) { grid-template-columns: repeat(5, 1fr); } /* 5+5 */
    &:has(.color-swatch:nth-child(11):last-child) { grid-template-columns: repeat(4, 1fr); } /* 4+4+3 */
    &:has(.color-swatch:nth-child(13):last-child) { grid-template-columns: repeat(5, 1fr); } /* 5+5+3 */
  }

  .color-swatch {
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
      outline: 2px solid var(--color-default-stroke);
      outline-offset: 0px;
    }
    
    &.selected {
      outline: 2px solid var(--editing);
      outline-offset: 0px;
    }
  }

</style>
